import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { createIdempotencyKey } from "@src/packages/care/cryovault/useSpecimens";
import type { paths } from "@src/packages/openapi/schema";

export { getFETBetaOrderDate, isFETWaitEligible } from "./fetBetaWait";

type FETPlansResponse =
  paths["/patients/{patientId}/frozen-embryo-transfer-plans"]["get"]["responses"]["200"]["content"]["application/json"];
type FETProceduresResponse =
  paths["/patients/{patientId}/frozen-embryo-transfer-plans/{planId}/procedures"]["get"]["responses"]["200"]["content"]["application/json"];
type FETBetaOrdersResponse =
  paths["/patients/{patientId}/frozen-embryo-transfer-procedures/{procedureId}/beta-hcg-orders"]["get"]["responses"]["200"]["content"]["application/json"];
type FETBetaResultBody = NonNullable<
  paths["/patients/{patientId}/frozen-embryo-transfer-procedures/{procedureId}/beta-hcg-orders/{orderId}/results"]["post"]["requestBody"]
>["content"]["application/json"];
type FETBetaResultResponse =
  paths["/patients/{patientId}/frozen-embryo-transfer-procedures/{procedureId}/beta-hcg-orders/{orderId}/results"]["post"]["responses"]["200"]["content"]["application/json"];
type FETBetaOrderBody = NonNullable<
  paths["/patients/{patientId}/frozen-embryo-transfer-procedures/{procedureId}/beta-hcg-orders"]["post"]["requestBody"]
>["content"]["application/json"];
type FETBetaOrderResponse =
  paths["/patients/{patientId}/frozen-embryo-transfer-procedures/{procedureId}/beta-hcg-orders"]["post"]["responses"]["200"]["content"]["application/json"];

export type FETPlan = FETPlansResponse["data"][number];
export type FETProcedure = FETProceduresResponse["data"][number];
export type FETBetaOrder = FETBetaOrdersResponse["data"][number];
export type FETBetaResult = FETBetaOrder["results"][number];

export type RecordFETBetaResultInput = {
  cycleId: string;
  procedureId: string;
  orderId: string;
  value: number;
  collectionAt?: string | null;
  collectionTimeUnknown?: boolean;
  timeZone?: string;
  sourceDocumentId?: string | null;
  clientRequestId?: string;
};

export type CreateFETBetaOrderInput = Pick<
  FETBetaOrderBody,
  "orderedAt" | "collectionWindowStart" | "collectionWindowEnd" | "status"
> & {
  cycleId: string;
  procedureId: string;
  clientRequestId?: string;
};

export class FETBetaOrderUnavailableError extends Error {
  constructor() {
    super("The FET cycle has no recordable beta order");
    this.name = "FETBetaOrderUnavailableError";
  }
}

export class FETBetaOrderLookupPendingError extends Error {
  constructor() {
    super("The FET beta order lookup has not finished");
    this.name = "FETBetaOrderLookupPendingError";
  }
}

export type FETBetaLifecycle = {
  plan: FETPlan;
  procedure: FETProcedure;
  orders: FETBetaOrder[];
};

const recordableBetaOrderStatuses: FETBetaOrder["status"][] = [
  "collected",
  "completed",
];

export function fetBetaLifecycleQueryKey(
  patientId: string | null,
  cycleId: string | null,
) {
  return ["fet-beta-lifecycle", patientId, cycleId] as const;
}

export function selectLatestFETBetaOrder(
  lifecycle: FETBetaLifecycle | null | undefined,
) {
  const order = lifecycle?.orders[0] ?? null;
  return order &&
    ["ordered", "scheduled", "collected", "completed"].includes(order.status)
    ? order
    : null;
}

export function selectRecordableFETBetaOrder(
  lifecycle: FETBetaLifecycle | null | undefined,
) {
  const order = selectLatestFETBetaOrder(lifecycle);
  return order && recordableBetaOrderStatuses.includes(order.status)
    ? order
    : null;
}

function latestByDate<T extends { updatedAt: string }>(items: T[]) {
  return [...items].sort((left, right) =>
    right.updatedAt.localeCompare(left.updatedAt),
  );
}

export function useFETBetaLifecycle(options: {
  cycleId: string | null;
  enabled?: boolean;
}) {
  const { client, patientId } = useApiClient();
  const enabled = options.enabled ?? true;

  return useQuery<FETBetaLifecycle | null>({
    queryKey: fetBetaLifecycleQueryKey(patientId, options.cycleId),
    queryFn: async () => {
      if (!patientId || !options.cycleId) return null;

      const plansResponse = await client.GET(
        "/patients/{patientId}/frozen-embryo-transfer-plans",
        { params: { path: { patientId } } },
      );
      if (plansResponse.error) {
        throwApiError(
          plansResponse.error,
          "Failed to load FET treatment plans",
          "fet-beta-lifecycle-plans",
        );
      }

      const plan = plansResponse.data?.data.find(
        (candidate) =>
          candidate.snapshots.some(
            (snapshot) => snapshot.cycleId === options.cycleId,
          ) ||
          candidate.versions.some(
            (version) => version.sourceCycleId === options.cycleId,
          ),
      );
      if (!plan) return null;

      const proceduresResponse = await client.GET(
        "/patients/{patientId}/frozen-embryo-transfer-plans/{planId}/procedures",
        { params: { path: { patientId, planId: plan.id } } },
      );
      if (proceduresResponse.error) {
        throwApiError(
          proceduresResponse.error,
          "Failed to load FET procedures",
          "fet-beta-lifecycle-procedures",
        );
      }

      const procedure = latestByDate(
        (proceduresResponse.data?.data ?? []).filter(
          (candidate) =>
            candidate.cycleId === options.cycleId &&
            candidate.recordStatus === "current",
        ),
      )[0];
      if (!procedure) return null;

      const ordersResponse = await client.GET(
        "/patients/{patientId}/frozen-embryo-transfer-procedures/{procedureId}/beta-hcg-orders",
        { params: { path: { patientId, procedureId: procedure.id } } },
      );
      if (ordersResponse.error) {
        throwApiError(
          ordersResponse.error,
          "Failed to load FET beta orders",
          "fet-beta-lifecycle-orders",
        );
      }

      return {
        plan,
        procedure,
        orders: latestByDate(ordersResponse.data?.data ?? []),
      };
    },
    enabled: enabled && !!patientId && !!options.cycleId,
  });
}

export function useRecordFETBetaResult() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<FETBetaResultResponse, Error, RecordFETBetaResultInput>({
    mutationFn: async ({
      cycleId,
      procedureId,
      orderId,
      value,
      collectionAt,
      collectionTimeUnknown = true,
      timeZone = "UTC",
      sourceDocumentId,
      clientRequestId,
    }) => {
      if (!patientId) {
        throw new Error("Cannot record FET beta result: missing patient");
      }

      const body: FETBetaResultBody = {
        status: "final",
        value,
        unit: "mIU/mL",
        ...(collectionAt ? { collectionAt } : {}),
        collectionTimeUnknown,
        resultAt: new Date().toISOString(),
        timeZone,
        sourceKind: "patient_reported",
        clientRequestId: clientRequestId ?? createIdempotencyKey(),
        ...(sourceDocumentId ? { sourceDocumentId } : {}),
      };
      const response = await client.POST(
        "/patients/{patientId}/frozen-embryo-transfer-procedures/{procedureId}/beta-hcg-orders/{orderId}/results",
        {
          params: { path: { patientId, procedureId, orderId } },
          body,
        },
      );
      if (response.error) {
        throwApiError(
          response.error,
          "Failed to record FET beta result",
          "fet-beta-result",
        );
      }
      if (!response.data) {
        throw new Error("FET beta result response was empty");
      }

      queryClient.invalidateQueries({
        queryKey: fetBetaLifecycleQueryKey(patientId, cycleId),
      });
      return response.data;
    },
  });
}

export function useCreateFETBetaOrder() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<
    FETBetaOrderResponse["data"],
    Error,
    CreateFETBetaOrderInput
  >({
    mutationFn: async ({
      cycleId,
      procedureId,
      orderedAt,
      collectionWindowStart,
      collectionWindowEnd,
      status = "ordered",
      clientRequestId,
    }) => {
      if (!patientId) {
        throw new Error("Cannot create FET beta order: missing patient");
      }

      const response = await client.POST(
        "/patients/{patientId}/frozen-embryo-transfer-procedures/{procedureId}/beta-hcg-orders",
        {
          params: { path: { patientId, procedureId } },
          body: {
            orderedAt,
            status,
            sourceKind: "patient_reported",
            clientRequestId: clientRequestId ?? createIdempotencyKey(),
            ...(collectionWindowStart !== undefined
              ? { collectionWindowStart }
              : {}),
            ...(collectionWindowEnd !== undefined
              ? { collectionWindowEnd }
              : {}),
          },
        },
      );
      if (response.error) {
        throwApiError(
          response.error,
          "Failed to create FET beta order",
          "fet-beta-order",
        );
      }
      if (!response.data) {
        throw new Error("FET beta order response was empty");
      }

      await queryClient.invalidateQueries({
        queryKey: fetBetaLifecycleQueryKey(patientId, cycleId),
      });
      return response.data.data;
    },
  });
}
